import { shouldLogOutOnForbidden } from '@/lib/api/twoFactorEnrollment';
import {
  CompanyTagCreateRequestBody,
  CompanyTagUpdateRequestBody,
  CompanyTagBulkUploadRequestBody,
} from '@/interfaces/api/general/company';
import { generalApi } from '@/lib/api/general';
import {
  dispatchCheckApiError,
  dispatchLogOut,
  MainContext,
} from '@/store/main/actionsMain';
import { commitAddNotification } from '@/store/main/mutations';
import { AxiosError } from 'axios';
import { defineMessages } from '@/lib/i18n';
import { ApiErrorResponse } from '@/interfaces/api/general/errorResponses';
import sharedMessages from '@/lib/shared-messages';

const m = defineMessages({
  tagAdded: 'タグが追加されました。',
  tagDeleted: 'タグが削除されました',
  tagUpdated: 'タグが更新されました',
});

export const actionGetCompanyTags = async (
  context: MainContext,
  payload: {
    companyId: string;
  }
) => {
  try {
    return await generalApi.getCompanyTag(payload.companyId);
  } catch (error) {
    const axiosError = error as AxiosError<ApiErrorResponse>;
    await dispatchCheckApiError(context, axiosError);
    if (shouldLogOutOnForbidden(axiosError)) {
      await dispatchLogOut(context);
    }
    throw axiosError;
  }
};

export const actionDeleteCompanyTag = async (
  context: MainContext,
  payload: {
    id: number;
    companyId: string;
  }
) => {
  try {
    const response = await generalApi.deleteCompanyTag(
      payload.id,
      payload.companyId
    );
    if (response) {
      commitAddNotification(context, {
        messageKey: m.tagDeleted,
        color: 'success',
      });
    }
  } catch (error) {
    const axiosError = error as AxiosError<ApiErrorResponse>;
    await dispatchCheckApiError(context, axiosError);
    if (shouldLogOutOnForbidden(axiosError)) {
      await dispatchLogOut(context);
    }
    throw axiosError;
  }
};

export const actionUpdateCompanyTag = async (
  context: MainContext,
  payload: {
    id: number;
    companyId: string;
    body: CompanyTagUpdateRequestBody;
  }
) => {
  try {
    const response = await generalApi.updateCompanyTag(
      payload.id,
      payload.companyId,
      payload.body
    );
    if (response) {
      commitAddNotification(context, {
        messageKey: m.tagUpdated,
        color: 'success',
      });
    }
  } catch (error) {
    const axiosError = error as AxiosError<ApiErrorResponse>;
    await dispatchCheckApiError(context, axiosError);
    if (shouldLogOutOnForbidden(axiosError)) {
      await dispatchLogOut(context);
    }
    throw axiosError;
  }
};

export const actionCreateCompanyTag = async (
  context: MainContext,
  payload: {
    payload: CompanyTagCreateRequestBody;
    companyId: string;
  }
) => {
  try {
    const response = await generalApi.createCompanyTag(
      payload.companyId,
      payload.payload
    );
    if (response) {
      commitAddNotification(context, {
        messageKey: m.tagAdded,
        color: 'success',
      });
    }
  } catch (error) {
    const axiosError = error as AxiosError<ApiErrorResponse>;
    await dispatchCheckApiError(context, axiosError);
    if (shouldLogOutOnForbidden(axiosError)) {
      await dispatchLogOut(context);
    }
    throw axiosError;
  }
};

export const actionBulkCreateCompanyTag = async (
  context: MainContext,
  payload: {
    payload: CompanyTagBulkUploadRequestBody;
    companyId: string;
  }
) => {
  try {
    const response = await generalApi.bulkCreateCompanyTags(
      payload.companyId,
      payload.payload
    );

    if (response) {
      const count = String(
        response.data?.tags?.length ?? payload.payload.tags.length
      );

      commitAddNotification(context, {
        messageKey: sharedMessages.tagsBulkUpload,
        messageValues: { count },
        color: 'success',
      });
    }
  } catch (error) {
    const axiosError = error as AxiosError<ApiErrorResponse>;
    await dispatchCheckApiError(context, axiosError);

    if (shouldLogOutOnForbidden(axiosError)) {
      await dispatchLogOut(context);
    }
    throw axiosError;
  }
};
